// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { Sort } from "@angular/material/sort";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize, take } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { CondoStructuresService } from "../../condo-structures.service";
import { PropertyEditNoteDialogComponent } from "../document-edit-note-dialog/document-edit-note-dialog.component";

/**
 * Script start
 */

@Component({
  selector: "app-property-detail-document-note-dialog",
  templateUrl: "./document-note-dialog.component.html",
  styleUrls: ["./document-note-dialog.component.scss"],
})
export class PropertyDetailDocumentNoteDialogComponent
  implements OnInit, OnDestroy
{
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = false; // Used to view/hide form loading spinner

  // BEGIN - Edit item form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  // END - Edit item form
  existingNotes: any[] = [];
  qrcodeData = "";
  documentArchiveIsLoading = false;
  sortedData = [];
  reload= false;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<PropertyDetailDocumentNoteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private configService: ConfigService,
    private propertiesService: PropertiesService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private condoStructuresService: CondoStructuresService,
    public confirmDialog: MatDialog
  ) {
    this.sortedData = this.data.notes.slice();
  }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Handle table sort
   *
   * @since 1.0.0
   */
  sortData(sort: Sort): any {
    const data = this.sortedData;
    if (!sort.active || sort.direction === "") {
      this.sortedData = data;
      return;
    }

    this.sortedData = data.sort((a, b) => {
      const isAsc = sort.direction === "asc";
      switch (sort.active) {
        case "structure":
          return this.compare(a.structure, b.structure, isAsc);
        case "name":
          return this.compare(a.name, b.name, isAsc);
        case "text":
          return this.compare(a.text, b.text, isAsc);
        case "createdAt":
          return this.compare(a.createdAt, b.createdAt, isAsc);
        case "createdBy":
          return this.compare(a.createdBy, b.createdBy, isAsc);
        default:
          return 0;
      }
    });
  }

  /**
   * Utility per table sort
   *
   * @since 1.0.0
   */
  compare(a: number | string, b: number | string, isAsc: boolean): any {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  /**
   * Handle table sort
   *
   * @since 1.0.0
   */
  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.sortedData = this.data.notes.slice();
    const regex = new RegExp(filterValue.trim().toLowerCase(), "gi");
    this.sortedData = this.data.notes.filter((el: any) => {
      const d = new Date(el.createdAt);
      const dStr =
        (d.getDate() < 10 ? "0" + d.getDate() : d.getDate()) +
        "/" +
        (d.getMonth() + 1 < 10 ? "0" + (d.getMonth() + 1) : d.getMonth() + 1) +
        "/" +
        d.getFullYear() +
        " " +
        d.getHours() +
        ":" +
        d.getMinutes() +
        ":" +
        d.getSeconds();
      const ret =
        el.structure.search(regex) > -1 ||
        el.name.search(regex) > -1 ||
        el.text.search(regex) > -1 ||
        dStr.search(regex) > -1 ||
        el.createdBy.search(regex) > -1;
      return ret;
    });
    //this.sortedData.filter = filterValue.trim().toLowerCase();
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(): void {
    const ret = {
      reload: this.reload
    }
    this.dialogRef.close(ret);
  }

  /**
   * Handle edit
   *
   * @since 1.0.0
   */
  onEdit(
    title: string,
    text: string,
    structureName: string,
    structureId: string,
    noteId: string
  ): void {
    const editDialogRef = this.confirmDialog.open(
      PropertyEditNoteDialogComponent,
      {
        minWidth: "40%",
        maxHeight: "98vh",
        data: {
          title,
          condoStructureId: this.data.condoStructureId,
          text,
          structureName,
          structureId,
          noteId,
        },
        disableClose: true,
      }
    );
    editDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res || !res.status) {
          return;
        }
        const index = this.sortedData.findIndex((el: any) => {
          return (
            el.structureName === structureName &&
            el.structureId === structureId &&
            el.noteId === noteId
          );
        });
        if (index > -1) {
          this.sortedData[index].text = res.text;
        }
        this.reload= true;
        return;
      });
  }

  /**
   * Handle delete
   *
   * @since 1.0.0
   */
  onDelete(structureName: string, structureId: string, noteId: string): void {
    const confirmDialogRef = this.confirmDialog.open(ConfirmDialogComponent, {
      minWidth: "30%",
      maxHeight: "98vh",
      data: {
        title: this.translateService.instant("common.warnings.warning"),
        message: this.translateService.instant("common.warnings.noteDelete"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      disableClose: true,
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    confirmDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.condoStructuresService
            .deleteNote(
              this.data.condoStructureId,
              structureName,
              structureId,
              noteId
            )
            .pipe(finalize(() => (this.formBtnOptions.active = false)))
            .subscribe(
              (res) => {
                if (res.status) {
                  this.toastrService.success(res.message);
                  const index = this.sortedData.findIndex((el: any) => {
                    return (
                      el.structureName === structureName &&
                      el.structureId === structureId &&
                      el.noteId === noteId
                    );
                  });
                  if (index > -1) {
                    this.sortedData.splice(index, 1);
                  }
                  this.reload= true;
                }
              },
              (err) => {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                // this.helperService.handleError(err);
                this.helperService.handleFormlyError(this.form, err);
              }
            )
        );
        return;
      });
  }
}
